import { useEffect, useMemo, useRef, useState } from "react";
import { Lock } from "lucide-react";
import type { Cluster, Response } from "../lib/types";

interface Props {
  clusters: Cluster[];
  /** All visible responses for the active question, keyed by cluster_id. */
  responsesByCluster?: Map<string, Response[]>;
  /** Whether the question's cluster scheme is frozen. */
  isFrozen?: boolean;
  /** Called once per cluster with its rendered card element (or `null` on unmount). */
  registerClusterRef?: (clusterId: string, el: HTMLElement | null) => void;
  /**
   * When a response just landed on a cluster card, the StageView pushes its
   * cluster id here briefly (~300ms). The matching card scales 1.0 → 1.04 →
   * 1.0 to acknowledge the placement. Independent of the run-id-keyed
   * `animate-cluster-pulse` used on full re-clusters.
   */
  pulseClusterId?: string | null;
}

function gridClassesFor(count: number): string {
  // Pick the grid layout from the ACTUAL cluster count, not the configured
  // max. Stage Calm guarantees count ∈ {4, 6, 8} (3/5/7 themed plus one
  // catch-all) but legacy runs can return anything; we treat the count we
  // see as authoritative so a 4-card render is always 2x2, never 3+1.
  if (count <= 4) return "grid-cols-2 grid-rows-2";
  if (count <= 6) return "grid-cols-3 grid-rows-2";
  return "grid-cols-4 grid-rows-2";
}

function sortForDisplay(clusters: Cluster[]): Cluster[] {
  // Put the catch-all (is_catchall = true) in the last grid slot regardless of
  // rank_order. The themed clusters keep their rank_order ordering. Legacy
  // runs (no catch-all row) just sort by rank_order naturally.
  const themed = clusters
    .filter((c) => !c.is_catchall)
    .sort((a, b) => a.rank_order - b.rank_order);
  const catchall = clusters.find((c) => c.is_catchall) ?? null;
  return catchall ? [...themed, catchall] : themed;
}

type Accent = {
  bg: string;
  size: string;
  ring: string;
  label: string;
  bullet: string;
};

// Anchor accent for the cluster with the highest live count — solid Berry
// block. As placements arrive and the leader shifts, the Berry follows.
const ANCHOR: Accent = {
  bg: "bg-rc-berry",
  size: "text-rc-paper",
  ring: "border-rc-paper/0",
  label: "text-rc-paper",
  bullet: "text-rc-paper/55",
};

// Single supporting accent for every non-anchor themed cluster. One coherent
// style means the only colour signal in the grid is the Berry anchor —
// "this is the biggest theme right now." Everything else is a neutral dark
// card. No lime in cluster cards (lime is reserved for the live-status
// indicator in the header).
const SUPPORTING: Accent = {
  bg: "bg-rc-paper/[0.04]",
  size: "text-rc-paper/80",
  ring: "border-rc-paper/15",
  label: "text-rc-paper",
  bullet: "text-rc-paper/30",
};

export default function ClusterDisplay({
  clusters,
  responsesByCluster,
  isFrozen = false,
  registerClusterRef,
  pulseClusterId = null,
}: Props) {
  // Cache a stable ref-callback per cluster id. Without this, the inline
  // `ref={(el) => registerClusterRef?.(cluster.id, el)}` creates a new
  // function on every render — React then invokes the old callback with
  // `null` (unmount) and the new one with the element (mount) every single
  // render. Combined with useStageCarousel's 100 ms progress tick that
  // re-renders the whole stage at 10 fps, the cluster-card ref map was
  // ping-ponging between empty and populated all the time. By memoising
  // the callback per cluster id we hand React the same function across
  // renders, so the ref binding sticks.
  const refCallbackCache = useRef<Map<string, (el: HTMLElement | null) => void>>(new Map());
  const getStableClusterRef = (clusterId: string) => {
    let cb = refCallbackCache.current.get(clusterId);
    if (!cb) {
      cb = (el: HTMLElement | null) => registerClusterRef?.(clusterId, el);
      refCallbackCache.current.set(clusterId, cb);
    }
    return cb;
  };

  const prevSizes = useRef<Map<string, number>>(new Map());
  const prevRunId = useRef<string | null>(null);
  const [pulseLabels, setPulseLabels] = useState<{ labels: Set<string>; token: number }>(
    { labels: new Set(), token: 0 },
  );

  useEffect(() => {
    if (clusters.length === 0) {
      prevSizes.current = new Map();
      prevRunId.current = null;
      return;
    }
    const currentRunId = clusters[0].run_id;
    const currentSizes = new Map(clusters.map((c) => [c.label, c.size]));

    if (prevRunId.current === null) {
      prevRunId.current = currentRunId;
      prevSizes.current = currentSizes;
      return;
    }

    if (currentRunId === prevRunId.current) return;

    const growers = new Set<string>();
    currentSizes.forEach((size, label) => {
      const before = prevSizes.current.get(label);
      if (before === undefined || size > before) growers.add(label);
    });

    prevRunId.current = currentRunId;
    prevSizes.current = currentSizes;

    if (growers.size === 0) return;
    setPulseLabels({ labels: growers, token: Date.now() });
  }, [clusters]);

  useEffect(() => {
    if (pulseLabels.labels.size === 0) return;
    const id = window.setTimeout(
      () => setPulseLabels({ labels: new Set(), token: 0 }),
      800,
    );
    return () => window.clearTimeout(id);
  }, [pulseLabels]);

  const ordered = useMemo(() => sortForDisplay(clusters), [clusters]);

  // Anchor selection against THEMED clusters only (catch-all is visually
  // distinct and shouldn't steal the anchor accent). Uses LIVE size from
  // responsesByCluster so the anchor shifts as the room thinks — the Berry
  // fill always marks whichever themed cluster is currently the biggest.
  // Falls back to cluster.size on legacy/empty data.
  const themedOrdered = useMemo(
    () => ordered.filter((c) => !c.is_catchall),
    [ordered],
  );
  const liveSizeOf = (c: Cluster) =>
    responsesByCluster?.get(c.id)?.length ?? c.size;
  const fullAnchorIdx = useMemo(() => {
    if (themedOrdered.length === 0) return -1;
    let idx = 0;
    let best = liveSizeOf(themedOrdered[0]);
    for (let i = 1; i < themedOrdered.length; i++) {
      const s = liveSizeOf(themedOrdered[i]);
      if (s > best) {
        idx = i;
        best = s;
      }
    }
    return idx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themedOrdered, responsesByCluster]);

  if (ordered.length === 0) return null;

  const gridCls = gridClassesFor(ordered.length);

  return (
    <div className={`grid ${gridCls} gap-4 h-full`}>
      {ordered.map((cluster, i) => {
        const isCatchall = cluster.is_catchall === true;
        // Anchor accent computed over themed clusters only. For catch-all we
        // override the chrome entirely below; everything else uses one
        // coherent SUPPORTING accent (no rotation through tints).
        const themedIdx = isCatchall ? -1 : i;
        const isAnchor = !isCatchall && themedIdx === fullAnchorIdx;
        const a = isAnchor ? ANCHOR : SUPPORTING;
        // Live count from realtime placements — falls back to the stored
        // cluster.size when no responsesByCluster map is provided. Drives the
        // displayed number, the bar width, and the dot cloud so all three
        // grow visibly as new placements arrive between cluster runs.
        const liveSize = responsesByCluster?.get(cluster.id)?.length ?? cluster.size;
        const isGrower = pulseLabels.labels.has(cluster.label);
        const isLandingPulse = pulseClusterId === cluster.id;
        // Per-placement landing pulse takes precedence over per-recluster growth
        // pulse — the live moment is more immediate. Both keyframes share the
        // `animation` CSS property, so applying both would simply hand the
        // shorter-priority class a no-op.
        const pulseClass = isLandingPulse
          ? "animate-cluster-land"
          : isGrower
            ? "animate-cluster-pulse"
            : "";
        const pulseKey = isGrower ? `${cluster.id}-${pulseLabels.token}` : cluster.id;

        const dots = responsesByCluster?.get(cluster.id) ?? [];
        const dotCount = dots.length > 0 ? dots.length : cluster.size;
        // Cap visible dots much earlier (was 40) and surface the remainder as
        // "x weitere…" text. Dozens of identical dots was noise, not
        // information; a short dot row + count overflow reads cleanly.
        const MAX_VISIBLE_DOTS = 8;
        const visibleDots = Math.min(dotCount, MAX_VISIBLE_DOTS);
        const overflowDots = dotCount - visibleDots;
        const lowConf =
          cluster.confidence != null && cluster.confidence < 0.5;

        // Catch-all swaps the solid accent border for dashed paper/20 and
        // overrides the surface/label so it reads as "the corner" of the grid
        // rather than a competing theme.
        const catchallSurface = "bg-rc-paper/5";
        const catchallBorder = "border-dashed border-rc-paper/20";
        const surfaceCls = isCatchall ? catchallSurface : a.bg;
        const borderCls = isCatchall
          ? catchallBorder
          : isFrozen
            ? "border-rc-lime/40"
            : a.ring;
        const labelCls = isCatchall ? "text-rc-paper" : a.label;

        return (
          <article
            key={pulseKey}
            ref={getStableClusterRef(cluster.id)}
            // Note: `animate-fade-up` is intentionally NOT on the article.
            // It and `animate-cluster-land` both write to the CSS `animation`
            // shorthand at equal specificity, and `animate-fade-up` happens
            // to be declared later in the compiled Tailwind output, so it
            // would silently win and the landing pulse would never run. The
            // grid-level entry can supply its own fade-up if needed; the
            // article itself stays animation-free at rest so cluster-land
            // owns the `animation` property when isLandingPulse fires.
            className={`relative rounded-rc-lg border-[1.5px] p-5 sm:p-6 flex flex-col gap-3 ${pulseClass} ${surfaceCls} ${borderCls} ${
              lowConf ? "opacity-70" : ""
            } ${isAnchor ? "shadow-hard" : ""}`}
          >
            {isFrozen && (
              <Lock
                className="absolute top-3 right-3 w-3.5 h-3.5 text-rc-lime/70"
                aria-label="Cluster fixiert"
              />
            )}
            <div className="flex items-start justify-between gap-3">
              <p
                className={`font-bold leading-[1.1] tracking-[-0.02em] ${labelCls}`}
                style={{ fontSize: "clamp(1.1rem, 1.95vw, 1.55rem)" }}
              >
                {isCatchall ? "Sonstiges" : cluster.label}
                {lowConf && !isCatchall && (
                  <span className="text-[12px] text-rc-paper/60 italic ml-2 font-normal">
                    (noch unklar)
                  </span>
                )}
              </p>
              <span
                className={`font-bold flex-shrink-0 tabular-nums inline-block origin-center ${
                  isCatchall ? "text-rc-stone" : a.size
                } ${isLandingPulse ? "animate-count-tick" : ""}`}
                style={{ fontSize: "clamp(0.95rem, 1.4vw, 1.2rem)" }}
              >
                {liveSize}
              </span>
            </div>

            {isCatchall && (
              <p className="text-[12px] italic text-rc-paper/55 -mt-1">
                Antworten ohne klares Thema
              </p>
            )}

            {!isCatchall && cluster.seed_origin && (
              <p className="text-[12px] italic text-rc-paper/55 -mt-1">
                aus Hypothese: {cluster.seed_origin}
              </p>
            )}

            {!isCatchall && (
              // Equal-length neutral divider below the cluster header.
              // Previously this was a percentage-fill "bar" encoding cluster
              // size relative to the max — but the same shape is used in
              // the carousel progress strip to encode TIMING, so a single
              // visual idiom carried two unrelated meanings. The count
              // number + dot cloud already convey size; the divider is now
              // purely structural. Thickness (h-0.5 / 2px) and opacity tuned
              // so the line reads on both the dark non-anchor surface and
              // the Berry anchor fill.
              <div
                className={`h-0.5 w-full rounded-full ${
                  isAnchor ? "bg-rc-paper/55" : "bg-rc-paper/30"
                }`}
              />
            )}

            {!isCatchall && cluster.representative_entries.length > 0 && (
              <ul className="flex flex-col gap-1">
                {cluster.representative_entries.slice(0, 4).map((entry, j) => (
                  <li
                    key={j}
                    className={`leading-[1.4] flex items-baseline gap-2 ${
                      isAnchor ? "text-rc-paper/85" : "text-rc-stone"
                    }`}
                    style={{ fontSize: "clamp(0.8rem, 1.05vw, 0.95rem)" }}
                  >
                    <span className={a.bullet}>·</span>
                    <span>{entry}</span>
                  </li>
                ))}
              </ul>
            )}

            {dotCount > 0 && (
              <div
                className="mt-1 flex flex-wrap gap-1.5 items-center"
                aria-label={`${dotCount} Antworten`}
              >
                {Array.from({ length: visibleDots }).map((_, i) => (
                  <span
                    key={i}
                    className={`inline-block w-2 h-2 rounded-full ${
                      isAnchor ? "bg-rc-paper/70" : "bg-rc-paper/45"
                    }`}
                  />
                ))}
                {overflowDots > 0 && (
                  <span
                    className={`text-[12px] italic ml-1 ${
                      isAnchor ? "text-rc-paper/70" : "text-rc-paper/50"
                    }`}
                  >
                    +{overflowDots} weitere
                  </span>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
